import React, { useState } from "react";
import { Phone, MessageCircle, Eye, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

// Normalize a phone for tel: and wa.me links. Cashfree-style: keep only digits,
// prefix +91 if the number is 10 digits (typical Indian mobile).
function normalizePhone(raw) {
  const digits = String(raw || "").replace(/\D/g, "");
  if (digits.length === 10) return { display: digits, tel: `+91${digits}`, wa: `91${digits}` };
  if (digits.length === 12 && digits.startsWith("91")) return { display: digits.slice(2), tel: `+${digits}`, wa: digits };
  return { display: digits, tel: `+${digits}`, wa: digits };
}

/**
 * ViewNumberButton — MagicBricks / 99acres style.
 *
 * Behaviour:
 *   1. If viewer is NOT logged in → "View Number" navigates to /login.
 *   2. If viewer is logged in and owner phone is available on the property (i.e. the
 *      backend already revealed it), we render the "View Number" CTA. On click we
 *      surface the number + two action buttons (Call + WhatsApp).
 *   3. If contact is still gated by the backend (contact_gated=true, no phone) we
 *      fall back to a subtle locked hint — parent decides the alternate path
 *      (e.g. SubscribeButton / Contact Owner form).
 */
export default function ViewNumberButton({ property, message }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [revealed, setRevealed] = useState(false);

  const isOwnListing = user?.id === property.owner_id;
  if (isOwnListing) return null;

  // Backend hasn't revealed the number — show a locked hint. Parent can render its
  // own unlock CTA next to it (Subscribe, Inquiry etc.).
  if (!property.owner_phone) {
    return (
      <div className="rounded-md bg-[#FDFBF7] border border-dashed border-[#1A1F2C]/15 p-3 text-xs text-[#5C6B7A] flex items-start gap-2" data-testid="view-number-locked">
        <Lock size={13} className="mt-0.5 shrink-0" />
        <span>Owner&rsquo;s number is protected. Send an inquiry or subscribe to unlock instantly.</span>
      </div>
    );
  }

  const phone = normalizePhone(property.owner_phone);
  const waText = message || `Hi ${property.owner_name || ""}, I'm interested in your listing on LocaNest — "${property.title}". Can we chat?`;

  if (!revealed) {
    return (
      <Button
        onClick={() => {
          if (!user) { navigate("/login"); return; }
          setRevealed(true);
        }}
        className="w-full bg-[#C85A32] hover:bg-[#a8482a] text-white rounded-md"
        data-testid="view-number-btn"
      >
        <Eye size={15} className="mr-2" /> View number
      </Button>
    );
  }

  return (
    <div className="space-y-2" data-testid="view-number-revealed">
      <div className="flex items-center gap-2 bg-[#FDFBF7] border border-[#1A1F2C]/10 rounded-md px-3 py-2 text-sm font-medium" data-testid="revealed-phone">
        <Phone size={14} className="text-[#C85A32]" />
        <a href={`tel:${phone.tel}`} className="tabular-nums tracking-wide">{phone.display}</a>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <a
          href={`tel:${phone.tel}`}
          className="inline-flex items-center justify-center gap-2 rounded-md bg-[#2B3A67] hover:bg-[#1f2c52] text-white px-3 py-2 text-sm font-medium"
          data-testid="call-owner-btn"
        >
          <Phone size={14} /> Call
        </a>
        <a
          href={`https://wa.me/${phone.wa}?text=${encodeURIComponent(waText)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 rounded-md bg-[#25D366] hover:bg-[#1ebe5b] text-white px-3 py-2 text-sm font-medium"
          data-testid="whatsapp-owner-btn"
        >
          <MessageCircle size={14} /> WhatsApp
        </a>
      </div>
    </div>
  );
}
